import {_, React, observer, observable, computed, IDisposer, action, useLocalStore, useEffect, useLayoutEffect, classNames, useAsObservableSource} from '@api'
import {GroupByGroupProductContents, GroupByGroupStoreContext, LoadingIndicator, PresentationModeContentStore, useVipLocalStore, VipLocalStore} from '@modules'
import {PresentationPageViewStore, usePresentationPageViewStore} from './'
import {IGroupByStoreNode, StoresInstance} from '@stores'
import {autorun, when} from 'mobx'
import * as css from './PresentationPageGroupContent.css'

/// region Store

export class PresentationPageGroupContentStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Omit<MyProps, 'className'> & { pageView: PresentationPageViewStore}) {
    super(stores)
  }

  get store() { return this.props.pageView}

  get page() { return this.store.page}

  get groupByNode() { return this.store.pageGroupNode}

  get groupByGroup() { return this.groupByNode?.store}

  registerEffects = () => {
    const {store} = this
    store.componentStores.groupContent = this
    let unexpandGroupOnUnmount = false

    const _toDispose: Array<IDisposer> = [
      autorun(() => {
        const {groupByNode} = this

        if (groupByNode?.store && !groupByNode.store.isExpanded) {
          unexpandGroupOnUnmount = true
          groupByNode.store.expand()
        }
      }),
      when(() => this.childGroupsLoaded, () => store.store.hasContentLoaded = true)
    ]

    return () => {
      _toDispose.forEach(d => d())
      if (unexpandGroupOnUnmount) {
        this.groupByNode?.store?.collapse()
      }
    }
  }

  @computed get childGroupsLoaded() {
    const {groupByGroup} = this
    if (!groupByGroup) return false

    if (!groupByGroup.hasChildGroup) return true

    return !groupByGroup.childGroupStore.mgr.initialLoad && groupByGroup.childGroupStore.childGroups.every(cg => cg?.productStore?.initialDataLoaded)
  }
}

export const PresentationPageGroupContentStoreContext = React.createContext<PresentationPageGroupContentStore>(null)
export const usePresentationPageGroupContentStore = () => React.useContext(PresentationPageGroupContentStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
}

export const PresentationPageGroupContent = observer<MyProps>(function PresentationPageGroupContent({className, ...props}) {
  const pageView = usePresentationPageViewStore(),
    obsProps = useAsObservableSource({...props, pageView}),
    store = useVipLocalStore((stores) => new PresentationPageGroupContentStore(stores, obsProps)),
    {childGroupsLoaded, groupByGroup, page, store: {}} = store

  useEffect(() => store.registerEffects(), [])

  return (
    <PresentationPageGroupContentStoreContext.Provider value={store}>
      <div className={classNames(css.root, className)} data-child-groups-loaded={childGroupsLoaded}>
        {!groupByGroup
         ? <LoadingIndicator/>
         : <GroupByGroupStoreContext.Provider value={groupByGroup}>
           {!childGroupsLoaded && <LoadingIndicator/>}
           <div className={css.contents}>
             {groupByGroup.hasChildGroup
              ? <div className={css.childGroups}
                     data-auto-columns={page.autoColumns ? true : undefined}
                     data-columns={page.autoColumns ? undefined : page.gridColumns}
                     style={{'--items': groupByGroup.childGroupStore.childGroups.length, '--grid-columns': page.gridColumns} as any}>
                {groupByGroup.childGroupStore.mgr.initialLoad && <LoadingIndicator/>}
                {groupByGroup.childGroupStore.childGroups.map((cg, i) => <ChildGroup group={cg} key={i}/>)}
              </div>
              : <GroupByGroupProductContents/>}
           </div>
         </GroupByGroupStoreContext.Provider>}
      </div>
    </PresentationPageGroupContentStoreContext.Provider>)
})

export const ChildGroup = observer<{ group: IGroupByStoreNode }>(function ChildGroup({group}) {
  if (group.store?.totalCount === 0) return null

  return (<div className={css.childGroup} data-type='ChildGroup'>
    {!group.store ? <LoadingIndicator />
     : <GroupByGroupStoreContext.Provider value={group.store}>
		 <div className={css.childGroupTitle}>{group.store.groupLabel}</div>
		 <div className={css.childContents}>
         <GroupByGroupProductContents/>
     </div>
	 </GroupByGroupStoreContext.Provider>}
  </div>)
})
/// endregion




// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/PresentationMode/pageContents/PresentationPageGroupContent.tsx